import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { CmsApiBlock } from '#/modules/cms/api/types/CmsApiBlock';
import { CmsBodyHolder } from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsBodyHolderWidth } from '#/modules/cms/components/BodyHolder/CmsBodyHolderWidth';
import type { CmsMappedBlock } from '#/modules/cms/mappers/CmsMappedBlock';
import { CmsMappedBlockWidth } from '#/modules/cms/mappers/CmsMappedBlockWidth';

import {
  CmsBlockContainerBox,
  cmsBlockContainerVariables,
} from './CmsBlockContainer.styled';

type Props<TBlock extends CmsApiBlock> = {
  id?: string;
  mappedBlock: CmsMappedBlock<TBlock>;
  prevBlockHasBackground: boolean;
  index: number;
  fullWidthLayout: boolean;
  showHeadingDivider: boolean;
  hideFirstHeadingDivider: boolean;
  children: ReactNode;
};

export function CmsMappedBlockContainer<TBlock extends CmsApiBlock>({
  id,
  mappedBlock,
  prevBlockHasBackground,
  index,
  fullWidthLayout,
  showHeadingDivider,
  hideFirstHeadingDivider,
  children,
}: Props<TBlock>) {
  const padding = useMemo(() => {
    let mobilePadding = 64;
    if (mappedBlock.block.type === 'THREE_STEP_CTA') {
      mobilePadding = 34;
    }
    return fullWidthLayout
      ? { base: index === 0 ? 36 : mobilePadding, smUp: 70 }
      : { base: 40, smUp: 60 };
  }, [fullWidthLayout, index, mappedBlock.block.type]);

  const headingDividerSpacing = fullWidthLayout
    ? { base: 64, smUp: 70 }
    : { base: 40, smUp: 60 };

  const display = useMemo(() => {
    if (mappedBlock.isHidden) return 'none';
    if (mappedBlock.isInline) return 'inline';
    return undefined;
  }, [mappedBlock.isHidden, mappedBlock.isInline]);

  const overflow = useMemo(() => {
    if (
      mappedBlock.block.type === 'SPLIT_CONTENT' &&
      mappedBlock.block.data.appearance === 'FULL_WIDTH'
    ) {
      return 'hidden';
    }

    return undefined;
  }, [mappedBlock]);

  const bodyHolderWidth = useMemo(() => {
    if (mappedBlock.width === CmsMappedBlockWidth.FULL) {
      return CmsBodyHolderWidth.FULL;
    }

    // All blocks are expanded when the content has full width layout
    if (fullWidthLayout || mappedBlock.type === 'SPLIT_CONTENT') {
      return CmsBodyHolderWidth.PAGE_LAYOUT;
    }

    if (
      mappedBlock.type === 'RECOMMENDED_JOBS' ||
      mappedBlock.type === 'RECOMMENDED_ACTIONS' ||
      mappedBlock.type === 'FOUR_ARTICLES_HIGHLIGHT'
    ) {
      return CmsBodyHolderWidth.NARROW_LAYOUT_WIDE;
    }

    return CmsBodyHolderWidth.NARROW_LAYOUT;
  }, [fullWidthLayout, mappedBlock.type, mappedBlock.width]);

  return (
    <CmsBlockContainerBox
      id={id}
      data-show-heading-divider={showHeadingDivider ? true : undefined}
      data-hide-first-heading-divider={
        hideFirstHeadingDivider ? true : undefined
      }
      data-has-background={mappedBlock.hasBackground ? true : undefined}
      data-type={mappedBlock.type}
      data-mb={
        !mappedBlock.padding || mappedBlock.padding === 'DEFAULT'
          ? true
          : undefined
      }
      data-mt={
        !mappedBlock.padding || mappedBlock.padding === 'DEFAULT'
          ? true
          : undefined
      }
      data-pt={
        mappedBlock.hasBackground && !(index > 0 && prevBlockHasBackground)
          ? true
          : undefined
      }
      data-pb={mappedBlock.hasBackground ? true : undefined}
      style={{
        display,
        overflow,
        ...cmsBlockContainerVariables({ padding, headingDividerSpacing }),
      }}
    >
      <CmsBodyHolder holderWidth={bodyHolderWidth}>{children}</CmsBodyHolder>
    </CmsBlockContainerBox>
  );
}
